import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  MenuItem, Container, Typography, TextField, Button, Paper, Stack, Box,
  Snackbar, Alert, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress,
  Tooltip, Avatar, useMediaQuery, useTheme, TablePagination
} from "@mui/material";
import {
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const BRANCH_URL = `${process.env.REACT_APP_API_URL}/api/tele/down`;

// ===== LIGHT THEME WITH YELLOW ACCENTS =====
const yellowButton = {
  background: "#FFD600",
  color: "#000000",
  fontWeight: "bold",
  "&:hover": {
    background: "#ffcc00",
    boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
  },
};

const lightStyles = {
  container: { minHeight: "100vh", bgcolor: "#f5f5f5", py: 2 },
  paper: {
    bgcolor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    color: "#000000",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  },
  dialogPaper: {
    bgcolor: "#ffffff",
    border: "1px solid #FFD600",
    color: "#000000",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      color: "#000000",
      backgroundColor: "#ffffff",
      "& fieldset": { borderColor: "#cccccc" },
      "&:hover fieldset": { borderColor: "#FFD600" },
      "&.Mui-focused fieldset": { borderColor: "#FFD600" },
    },
    "& .MuiInputLabel-root": { color: "#666666" },
    "& .MuiSelect-icon": { color: "#FFD600" }
  },
  tableHeader: {
    color: "#000000",
    fontWeight: "bold",
    borderBottom: "2px solid #FFD600",
    backgroundColor: "#f5f5f5"
  },
  tableCell: {
    color: "#000000",
    borderBottom: "1px solid #e0e0e0"
  },
  actionBtn: {
    background: "#FFD600",
    color: "#000000",
    fontWeight: "bold",
    "&:hover": {
      background: "#ffcc00",
      boxShadow: "0 0 15px #FFD600"
    }
  },
  yellowBorder: {
    borderColor: "#FFD600",
    "&:hover": {
      borderColor: "#ffcc00",
      backgroundColor: "rgba(255, 214, 0, 0.1)",
    }
  },
  yellowText: {
    color: "#FFD600",
    "&:hover": { color: "#ffcc00" }
  }
};

// Custom scrollbar styles
const scrollbarStyles = `
  *::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  *::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  *::-webkit-scrollbar-thumb {
    background: #FFD600;
    border-radius: 10px;
  }
  *::-webkit-scrollbar-thumb:hover {
    background: #ffcc00;
  }
  * {
    scrollbar-width: thin;
    scrollbar-color: #FFD600 #f1f1f1;
  }
`;

const BranchStatus = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // State
  const [branches, setBranches] = useState([]);
  const [branchSearch, setBranchSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedRowId, setSelectedRowId] = useState(null);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const showSnackbar = (msg, severity = "success") => setSnackbar({ open: true, message: msg, severity });

  // ===== Fetch Branches from /down/branches endpoint =====
  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BRANCH_URL}/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // The endpoint returns { branches: [...] }
      setBranches(Array.isArray(res.data.branches) ? res.data.branches : []);
    } catch (err) {
      showSnackbar("Failed to fetch branches", "error");
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBranches();

    const intervalId = setInterval(() => {
      const minute = new Date().getMinutes();
      if (minute % 4 === 2) {
        fetchBranches();
      }
    }, 1 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [fetchBranches]);

  function formatThis(dateValue) {
    if (!dateValue) return "—";

    const d = new Date(dateValue);
    if (isNaN(d)) return "—";

    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).replace(",", "");
  }

  function formatDuration(totalMinutes) {
    if (!totalMinutes && totalMinutes !== 0) return "—";

    const days = Math.floor(totalMinutes / (24 * 60));
    totalMinutes %= 24 * 60;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    let result = "";
    if (days > 0) result += `${days}d `;
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0) result += `${minutes}m`;

    return result.trim() || "0m";
  }

  const filteredBranches = useMemo(() => {
    let filtered = branches.filter(b =>
      Object.values(b).join(" ").toLowerCase().includes(branchSearch.toLowerCase())
    );

    if (!statusFilter || statusFilter === "All") return filtered;

    const filterLower = statusFilter.toLowerCase();

    const matchRules = {
      "down": (status) => status && status.toLowerCase().includes("down"),
      "intermittent": (status) => status && status.toLowerCase().includes("int"),
      "active": (status) => status && (status.toLowerCase().includes("active") || status.toLowerCase().includes("sym"))
    };

    const matchFunction = matchRules[filterLower];
    if (matchFunction) {
      return filtered.filter(b => matchFunction(b.status));
    }

    return filtered;
  }, [branches, branchSearch, statusFilter]);

  const paginatedBranches = useMemo(() => {
    return filteredBranches.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredBranches, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const lightPaper = {
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    color: "#000000",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)"
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", py: 2 }}>

      <Container
        maxWidth={false}
        sx={{
          width: { xs: "100%", md: "calc(100% - 96px)" },
          ml: { xs: 0, md: "84px" },
          mr: { xs: 0, md: "12px" },
          px: { xs: 1, md: 0.5 },
        }}
      >
    {/* --- FILTER BAR --- */}
        <Paper
          sx={{
            p: 1.5,
            mb: 2,
            ...lightPaper,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2
          }}
        >
          {/* LEFT GROUP: SEARCH + COUNT + REFRESH */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: isMobile ? 1 : 0 }}>
            <TextField
              placeholder="Search..."
              size="small"
              sx={{
                ...lightStyles.input,
                width: isMobile ? 200 : 300,
              }}
              value={branchSearch}
              onChange={(e) => setBranchSearch(e.target.value)}
            />

            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="caption"
                sx={{
                  color: "#000000",
                  fontWeight: "bold",
                  border: "1px solid #e0e0e0",
                  px: 1,
                  py: 0.5,
                  borderRadius: "4px",
                  bgcolor: "rgba(255,214,0,0.05)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "36px"
                }}
              >
                {filteredBranches.length}
              </Typography>

              <IconButton
                onClick={async () => {
                  await fetchBranches();
                  showSnackbar("Table updated", "success");
                }}
                sx={{ ...yellowButton, background: "transparent", border: "1px solid #FFD600", flexShrink: 0 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
          </Stack>

          {/* RIGHT GROUP: STATUS FILTER */}
          <Stack direction="row" spacing={1.5}>
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label={isMobile ? "" : "Filter State"}
              sx={{ ...lightStyles.input, width: isMobile ? 140 : 160 }}
            >
              <MenuItem value="All">All Status</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Intermittent">Intermittent</MenuItem>
              <MenuItem value="Down">Down</MenuItem>
            </TextField>
          </Stack>
        </Paper>

        {/* --- DATA TABLE --- */}
        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 240px)",
            overflowX: "auto",
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": { bgcolor: "#f5f5f5" },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#FFD600", borderRadius: "4px" },
            "&::-webkit-scrollbar:horizontal": { height: "8px" },
            "&::-webkit-scrollbar-track:horizontal": { bgcolor: "#f5f5f5" },
            "&::-webkit-scrollbar-thumb:horizontal": { bgcolor: "#FFD600", borderRadius: "4px" }
          }}
        >
          {loading ? (
            <Box p={10} textAlign="center">
              <CircularProgress sx={{ color: "#FFD600" }} />
            </Box>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["Name", "Company", "Service #", "WAN IP", "Status", "Duration", "P.R", "Problem", "Actions", "Event Time"].map((h, i) => (
                    <TableCell key={i} sx={{ ...lightStyles.tableHeader, bgcolor: "#f5f5f5", whiteSpace: "nowrap" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedBranches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: "#666666" }}>
                      No branches with Down or Intermittent status
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedBranches.map((b) => (
                    <TableRow
                      key={b._id}
                      onClick={() => setSelectedRowId(prev => (prev === b._id ? null : b._id))}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { bgcolor: "rgba(255, 214, 0, 0.1)" },
                        ...(selectedRowId === b._id && {
                          bgcolor: b.status?.toLowerCase().includes("active")
                            ? "#00e67633"
                            : b.status?.toLowerCase().includes("int")
                              ? "#ff980033"
                              : "#ff444433",
                        }),
                      }}
                    >
                      <TableCell sx={{ ...lightStyles.tableCell, whiteSpace: "nowrap" }}>{b.name || "N/A"}</TableCell>
                      <TableCell sx={{ ...lightStyles.tableCell, whiteSpace: "nowrap" }}>{b.company || "N/A"}</TableCell>
                      <TableCell sx={{ ...lightStyles.tableCell, whiteSpace: "nowrap" }}>{b.serviceNumber || "N/A"}</TableCell>
                      <TableCell sx={{ ...lightStyles.tableCell, whiteSpace: "nowrap" }}>{b.wanIP || "N/A"}</TableCell>
                      <TableCell sx={{
                        ...lightStyles.tableCell,
                        whiteSpace: "nowrap",
                        fontWeight: "bold",
                        color: b.status?.toLowerCase().includes("active")
                          ? "#00e676"
                          : b.status?.toLowerCase().includes("int")
                            ? "#ff9800"
                            : "#ff4444"
                      }}>
                        {b.status || "N/A"}
                      </TableCell>
                      <TableCell sx={{ ...lightStyles.tableCell, whiteSpace: "nowrap" }}>{formatDuration(b.duration)}</TableCell>
                      <TableCell sx={{ ...lightStyles.tableCell, whiteSpace: "nowrap" }}>{b.pingResult || "—"}</TableCell>
                      <TableCell sx={{ ...lightStyles.tableCell, whiteSpace: "nowrap" }}>{b.problem || "—"}</TableCell>
                      <TableCell sx={{ ...lightStyles.tableCell, whiteSpace: "nowrap" }}>
                        {b.action === "Requiring" ? "Req" : b.action === "Pending" ? "Pen" : b.action === "Maintenance" ? "Mai" : b.action || "—"}
                      </TableCell>
                      <TableCell sx={{ ...lightStyles.tableCell, whiteSpace: "nowrap" }}>{formatThis(b.eventTime)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 200, 500, 1000]}
          component="div"
          count={filteredBranches.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: "#000000",
            borderTop: "1px solid #e0e0e0",
            "& .MuiTablePagination-selectIcon": { color: "#FFD600" },
            "& .MuiIconButton-root": { color: "#FFD600" }
          }}
        />

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity} sx={{ bgcolor: "#ffffff", color: "#000000", border: "1px solid #FFD600" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default BranchStatus;